import { COUPON_DISCOUNT_TYPE_V2, COUPON_PERIOD_TYPE_V2, COUPON_STATUS_V2, COUPON_TYPE_V2 } from './common';
import { DateRageType } from '../common/date';
import {
  CouponDto,
  CouponDtoAvailableTypeEnum,
  CouponDtoDiscountTypeEnum,
  CouponDtoPeriodTypeEnum,
  CouponDtoStatusEnum,
} from '../api/coupons';
import { Sort } from '../common/options';
import moment from '../../lib/moment';

/**
 *
 * 타입 제너레이터로 파라미터 타입은 생성되지 않아서 직접 구현
 * @export
 * @interface CouponRequestDto
 */
export type CouponRequestDto = Partial<{
  //기간 타입( fixedDate : 날짜 지정 , generatedDate : 발급일 기준 , infinity : 무기한 )
  periodType: CouponDtoPeriodTypeEnum;
  //할인 타입에 따른 혜택 수치(금액, 할인률 등)
  discountAmount: number;
  //최소 구매 금액(원 단위)
  minimumPurchaseAmount: number;
  //최대 할인 가능 금액 (원 단위)
  maximumDiscountAmount?: number;
  //발급일로부터 날짜 수
  dayPlus: number;
  //쿠폰 고유 코드
  code: string;
  //발급제한 기간 날짜 (0: 무제한, 1~ : 기간내에 몇회로 제한)
  generateLimitDay: number;
  //발급제한 횟수
  generateLimitCount: number;
  //쿠폰에 대한 메모 (구매자, 판매자에게는 노출되지 않고 운영자들이 간단히 남기는 내용)
  memo: string;
  //특정 브랜드 쿠폰의 24k상품 제외여부
  exclude24k: boolean;
  //쿠폰 식별 id
  id: number;
  //쿠폰 이름
  name: string;
  //쿠폰에 대한 설명
  description: string;
  //쿠폰 상태( inactive : 비활성 (노출 안함) , active : 활성 (노출 함) )
  status: CouponDtoStatusEnum | null;
  //쿠폰 적용 가능 범위( totalProductCoupon : 전체 상품 쿠폰 , targetProductCoupon : 특정 상품 쿠폰 , targetBrandCoupon : 특정 브랜드 쿠폰 )
  availableType: CouponDtoAvailableTypeEnum | null;
  //할인 타입( amount : 금액 , rate : 할인율 )
  discountType: CouponDtoDiscountTypeEnum | null;
  //쿠폰 생성 시작 일자
  createStartDate: string;
  //쿠폰 생성 종료 일자
  createEndDate: string;
  //쿠폰 유효기간 시작일자
  startDate: string;
  //쿠폰 유효기간 종료일자
  endDate: string;
  //페이지번호
  page: number;
  //페이지당 아이템 수
  pageSize: number;
  //정렬 가능한 field (id, status, name, createDate)
  sort: Sort<'id' | 'status' | 'name' | 'createDate'>;
}>;

export type CouponBenefit = {
  minimumPurchaseAmount: number;
  discountAmount: number;
  maximumDiscountAmount?: number;
};

export type CouponAvailablePeriod = {
  periodType: COUPON_PERIOD_TYPE_V2;
  period?: DateRageType;
  dayPlus?: number;
};

export class CouponItemV2 {
  id: number;
  name: string;
  description: string;
  discountType: COUPON_DISCOUNT_TYPE_V2;
  status: COUPON_STATUS_V2;
  type: COUPON_TYPE_V2;
  isExclude24k?: boolean;
  benefit: CouponBenefit;
  createDate: string;
  availablePeriod: CouponAvailablePeriod;

  constructor(item: CouponDto) {
    this.id = item.id;
    this.name = item.name;
    this.description = item.description;
    this.discountType = item.discountType;
    this.status = item.status;
    this.type = item.availableType;
    this.isExclude24k = item.exclude24k;
    this.benefit = {
      minimumPurchaseAmount: item.minimumPurchaseAmount,
      discountAmount: item.discountAmount,
      maximumDiscountAmount: item.maximumDiscountAmount,
    };
    this.createDate = item.createDate;
    this.availablePeriod = {
      periodType: item.periodType,
      period: item.date,
      dayPlus: item.dayPlus,
    };
  }
}

export type CouponListRequest = Partial<{
  discountType: CouponItemV2['discountType'] | null;
  status: CouponItemV2['status'] | null;
  type: CouponItemV2['type'] | null;
  id: CouponItemV2['id'];
  name: CouponItemV2['name'];
  description: CouponItemV2['description'];
  createDate: { startDate: string; endDate: string };
  availablePeriod: { startDate: string; endDate: string };
  page?: number;
  pageSize?: number;
}>;

export class CouponListRequestOption implements CouponRequestDto {
  periodType?: CouponDtoPeriodTypeEnum;
  discountAmount?: number;
  minimumPurchaseAmount?: number;
  maximumDiscountAmount?: number;
  dayPlus?: number;
  code?: string;
  generateLimitDay?: number;
  generateLimitCount?: number;
  memo?: string;
  exclude24k?: boolean;
  id?: number;
  name?: string;
  description?: string;
  status?: CouponDtoStatusEnum | null;
  availableType?: CouponDtoAvailableTypeEnum | null;
  discountType?: CouponDtoDiscountTypeEnum | null;
  createStartDate?: string;
  createEndDate?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
  sort?: Sort<'id' | 'status' | 'name' | 'createDate'>;
  constructor(option: CouponListRequest) {
    this.id = option.id;
    this.availableType = option.type;
    this.status = option.status;
    this.discountType = option.discountType;
    this.name = option.name;
    this.description = option.description;
    this.createStartDate =
      option.createDate?.startDate && moment(option.createDate?.startDate).startOf('day').toISOString();
    this.createEndDate = option.createDate?.endDate && moment(option.createDate?.endDate).endOf('day').toISOString();
    this.startDate =
      option.availablePeriod?.startDate && moment(option.availablePeriod?.startDate).startOf('day').toISOString();
    this.endDate =
      option.availablePeriod?.endDate && moment(option.availablePeriod?.endDate).endOf('day').toISOString();
    this.page = option.page;
    this.pageSize = option.pageSize;
  }
}
